import urls from "../global/constants/UrlConstants";
import { store } from "./store";
import { RESET_STORE, updateAccessToken, updateRefreshToken } from "../redux/authSlice";
import strings from "../global/constants/StringConstants";
import notifiers from "../global/constants/NotificationConstants";
import { isTruthy } from "../helpers/methods";
import history from "./history";
import { jwtDecode } from "jwt-decode";

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

export const checkStatus = async (error: any) => {
  if (error.status === 403 || error.status === 401) {
    store.dispatch({ type: RESET_STORE });
    history.push(urls.loginViewPath);
    return true;
  }
  return false;
};

// Authenticated Call Headers
export const getCallParams = async (methodType: string, body?: any) => {
  const accessToken = store.getState().auth.accessToken;
  const refreshToken = store.getState().auth.refreshToken;

  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, refreshToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
};

// Unauthenticated Call Headers
export const getNoAuthCallParams = (methodType: string, body?: any) => {
  return {
    method: methodType,
    headers: strings.applicationJSON,
    body: JSON.stringify(body),
  };
};

export const getFileCallParams = async (body: any) => {
  const accessToken = store.getState().auth.accessToken;
  const refreshToken = store.getState().auth.refreshToken;
  return {
    method: "POST",
    headers: await getHeaderObject(accessToken, refreshToken, strings.multipartForm),
    body: body,
  };
};

export const getNoAuthFileCallParams = (body: any) => {
  return {
    method: "POST",
    headers: strings.multipartForm,
    body: body,
  };
};

export async function getHeaderObject(accessToken: string, refreshToken: string, contentType: any) {
  try {
    const verifiedAccessToken: string | void = await checkIdToken(accessToken, refreshToken);
    if (verifiedAccessToken) {
      return {
        ...contentType,
        Authorization: "Bearer " + verifiedAccessToken,
      };
    }
    window.history.pushState(null, "", urls.homeViewPath);
    return null;
  } catch (error: any) {
    throw error;
  }
}

// Triggers the api call
// If the api call takes more time than timeout, then it will timeout the call.
export const makeCall = async (callName: string, callParams: any) => {
  try {
    let call = fetch(callName, callParams);
    let response: any;
    if (urls.BYPASS_TIMEOUT.some((url) => callName.includes(url))) {
      response = await Promise.race([call]).catch((error) => {
        throw error;
      });
    } else {
      let timeout = getTimeoutPromise();
      response = await Promise.race([timeout, call]).catch((error) => {
        throw error;
      });
    }

    const json = await response.json();
    if (response && response.ok) {
      return json;
    }
    throw json;
  } catch (error: any) {
    if (await checkStatus(error)) {
      throw { error: true, errorMessage: notifiers.LOGGEDOUT };
    }
    throw error;
  }
};

export async function checkIdToken(accessToken: string, refreshTok: string) {
  if (accessToken) {
    const decodedIdToken: any = jwtDecode(accessToken);
    let userEmail = decodedIdToken["email"];

    if (decodedIdToken["exp"] * 1000 <= Date.now()) {
      if (isRefreshing && refreshPromise) {
        return refreshPromise;
      }

      isRefreshing = true;
      refreshPromise = refreshToken(refreshTok, userEmail)
        .then((newToken) => {
          isRefreshing = false;
          refreshPromise = null;
          return newToken;
        })
        .catch((error) => {
          isRefreshing = false;
          refreshPromise = null;
          throw error;
        });

      return refreshPromise;
    }
    return accessToken;
  }
}

export async function refreshToken(refreshTok: string, email: string) {
  try {
    const body = {
      userName: email,
    };
    const lang = store.getState().auth.locale;
    const url = `${urls.REFRESH_TOKEN}/${refreshTok}/lang/${lang}`;
    const callParams = getNoAuthCallParams("POST", body);
    const response: any = await makeCall(url, callParams);
    store.dispatch(
      updateAccessToken({
        accessToken: response.authToken,
      }),
    );
    store.dispatch(
      updateRefreshToken({
        refreshToken: response.refreshToken,
      }),
    );
    return response.authToken;
  } catch (error: any) {
    if (!isTruthy(error.message)) {
      throw {
        error: true,
        message: notifiers.GENERIC_ERROR,
      };
    }
    throw error;
  }
}

export const getTimeoutPromise = () => {
  return new Promise((resolve, reject) => {
    setTimeout(
      () =>
        reject({
          error: true,
          message:
            store.getState().auth.locale === "en"
              ? "Something went wrong. Please reload the page."
              : "Un problème est survenu. Veuillez réessayer plus tard.",
        }),
      20000, // 20 Seconds
    );
  });
};

export async function getAccessToken(methodType: string, body?: any, token?: string) {
  const accessToken = token!;
  return {
    method: methodType,
    headers: await getHeaderObject(accessToken, accessToken, strings.applicationJSON),
    body: JSON.stringify(body),
  };
}

export async function getDynamicRoutesData() {
  try {
    const locale = store.getState().auth.locale;
    const url = urls.DYNAMIC_ROUTES + "/" + locale;
    const callParams = getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function getDynamicRoutesDataForAdmin(page: number) {
  try {
    const url = urls.ADMIN_DYNAMIC_ROUTES + "/" + page;
    const callParams = getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export const imageRenderer = (templateId: string, fileName: string) => {
  if (isTruthy(fileName)) {
    return urls.getImage + "/" + templateId + "/" + fileName;
  }
  return "";
};

export const isVideo = (filename: string) => {
  const extension = filename.split(".")[1];
  return extension === "mp4";
};
